import canonicalize from 'canonicalize';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex } from '@noble/hashes/utils.js';
import type { EvidenceManifestV1, JournalEntry } from './types';

export const MAX_FILE_BYTES = 25_000_000;
export const hex = bytesToHex;

export async function sha256Hex(data: Uint8Array<ArrayBufferLike> | ArrayBuffer): Promise<string> {
  const bytes = data instanceof ArrayBuffer
    ? new Uint8Array(data)
    : new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  return bytesToHex(sha256(bytes));
}

export function canonicalBytes(value: unknown): Uint8Array {
  const encoded = canonicalize(value);
  if (encoded === undefined) throw new Error('Манифест нельзя канонизировать по RFC 8785');
  return new TextEncoder().encode(encoded);
}

export function createManifest(input: Omit<EvidenceManifestV1, 'format' | 'version' | 'deviceTimeTrust' | 'browser' | 'location'>): EvidenceManifestV1 {
  return {
    format: 'digevidence-manifest', version: 1, deviceTimeTrust: 'untrusted',
    browser: { userAgent: navigator.userAgent, language: navigator.language, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone },
    location: { status: 'not_requested' }, ...input,
  };
}

export async function generateEvidenceKey(): Promise<CryptoKeyPair> {
  return crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify']) as Promise<CryptoKeyPair>;
}

export async function signBytes(key: CryptoKey, bytes: Uint8Array): Promise<Uint8Array> {
  const owned = new Uint8Array(bytes.byteLength);
  owned.set(bytes);
  return new Uint8Array(await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, key, owned.buffer));
}

export async function appendLocalJournal(current: JournalEntry[], recordId: string, event: string, payload: JournalEntry['payload']): Promise<JournalEntry> {
  const previous = current.at(-1);
  const core = { seq: (previous?.seq ?? 0) + 1, recordId, event, at: new Date().toISOString(), payload, previousHash: previous?.hash ?? '0'.repeat(64) };
  const hash = await sha256Hex(canonicalBytes(core));
  return { ...core, hash };
}
